import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Message, Modal, Spin } from '@arco-design/web-react';
import { IconRight } from '@arco-design/web-react/icon';

import { ConfigContext } from '@/components/ConfigProvider/configProvider';
import { useSystemMenuState, useTenantMenuState } from '@/hooks/useMenu';
import { NAV_MAP } from '@/hooks/useMenu/constants';
import { getFirstAccessibleConsolePath } from '@/hooks/useMenu/subCenter';
import { getModule } from '@/hooks/useRouteScope';
import { STORAGE_KEYS, getDcAndValByTenant, getDcListAndVal } from '@/layout/servies';
import { _setStorage } from '@/utils/useLocalStorage';

import styles from '../styles/index.module.less';

/** 管理空间仅三种：系统、数据中心、租户 */
export type ConsoleMode = 'system' | 'data-center' | 'console';

const MODE_ITEMS = [
  { key: 'system', image: '/images/system.png', navKey: 'systemSettingsMode' },
  { key: 'data-center', image: '/images/data-center.png', navKey: 'dataCenterMode' },
  { key: 'console', image: '/images/tenant.png', navKey: 'console' },
] as const;

/** 一级路径 → 管理空间；集群页走数据中心，工作空间集群页走租户 */
const MODULE_MODE_MAP: Record<string, ConsoleMode> = {
  system: 'system',
  'data-center': 'data-center',
  cluster: 'data-center',
  console: 'console',
  wsp: 'console',
};

/** URL 可明确推断的管理模式优先于 localStorage */
export function resolveModeFromPathname(pathname?: string): ConsoleMode | undefined {
  return MODULE_MODE_MAP[getModule(pathname)];
}

export function persistConsoleMode(mode?: ConsoleMode) {
  if (!mode) {
    localStorage.removeItem(STORAGE_KEYS.MODE);
    return;
  }
  _setStorage(STORAGE_KEYS.MODE, mode);
}

function isConsoleMode(value: unknown): value is ConsoleMode {
  return value === 'system' || value === 'data-center' || value === 'console';
}
const getFirstpath = (menu) => {
  if (menu?.children?.length > 0) {
    return getFirstpath(menu?.children?.[0]);
  }
  return menu?.path;
};

export const getModeMap = ({
  systemMenu,
  clusterList,
  tenantList,
  cluster,
  tenant,
  tenantCluster,
  tenantClusters,
  tenantMenu,
}: {
  systemMenu?: { children?: unknown[]; path?: string };
  clusterList?: { name: string }[];
  tenantList?: unknown[];
  cluster?: string;
  tenant?: string;
  tenantCluster?: string;
  tenantClusters?: { name: string }[];
  /** 过滤后的租户菜单；未就绪时传 undefined，避免先落到无权限的总览 */
  tenantMenu?: { children?: unknown[] }[];
}) => {
  const map: Record<string, string> = {};
  const systemPath = getFirstpath(systemMenu);
  if (systemPath) {
    map.system = systemPath;
  }
  if (clusterList?.length > 0 && cluster) {
    const item = clusterList.find((i) => i.name === cluster);
    map['data-center'] = `/data-center/${item?.name || clusterList?.[0]?.name}/overview`;
  }
  if (tenantList?.length > 0 && tenant && tenantMenu !== undefined) {
    const t = tenantCluster && tenantCluster !== 'undefined' ? tenantCluster : ':cluster';
    const item = tenantClusters?.find((i) => i.name === t);
    // 避免 item / 列表皆空时模板字符串把 undefined 写成路径 "undefined"
    const clusterName = item?.name || tenantClusters?.[0]?.name || ':cluster';
    const firstPath = getFirstAccessibleConsolePath(tenantMenu?.[0]);
    map.console = firstPath ?? `/console/${tenant}/cluster/${clusterName}/overview`;
  }
  return map;
};

/** 管理空间及其就绪状态：初始化与系统菜单权限计算完成前不判定为空。 */
export const useModeState = () => {
  const { tenantList, clusterList, tenant, tenantCluster, cluster, tenantClusters, loading: initialLoading } = useContext(ConfigContext);
  const { menu: systemMenu, loading: systemMenuLoading } = useSystemMenuState();
  const { menu: tenantMenu, loading: tenantMenuLoading } = useTenantMenuState();
  const { pathname } = useLocation();

  const modeMap = useMemo(() => {
    return getModeMap({
      systemMenu,
      clusterList,
      tenantList,
      tenant,
      tenantCluster,
      cluster,
      tenantClusters,
      tenantMenu: tenantMenuLoading ? undefined : tenantMenu,
    });
  }, [systemMenu, clusterList, tenantList, tenant, tenantCluster, cluster, tenantClusters, tenantMenu, tenantMenuLoading, pathname]);

  return { modeMap, loading: initialLoading || systemMenuLoading || tenantMenuLoading };
};

export const useModes = () => {
  return useModeState().modeMap;
};

function ModeCard({
  image,
  title,
  description,
  actionText,
  current,
  onClick,
}: {
  image: string;
  title: string;
  description: string;
  actionText: string;
  current: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.modeCard} ${current ? styles.modeCardCurrent : ''}`}
      aria-current={current ? 'page' : undefined}
      onClick={onClick}
    >
      <img className={styles.modeCardIcon} alt="" src={image} />
      <div className={styles.modeCardTitleRow}>
        <div className={styles.modeCardTitle}>{title}</div>
        {current && <span className={styles.modeCardCurrentTag}>{actionText}</span>}
      </div>
      <div className={styles.modeCardDesc}>{description}</div>
      {!current && (
        <div className={styles.modeCardAction}>
          {actionText}
          <IconRight />
        </div>
      )}
    </button>
  );
}

export default function ChangeModeContent({ modeMap, cb = undefined, resetMode = false }) {
  const { t } = useTranslation();
  const { setMode, loading, mode } = useContext(ConfigContext);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentMode = MODULE_MODE_MAP[getModule(pathname)];

  useEffect(() => {
    if (resetMode) {
      setMode(undefined);
      persistConsoleMode(undefined);
      return;
    }
    if (loading) return;

    const urlMode = resolveModeFromPathname(pathname);
    if (urlMode) {
      // 当前标签页以 URL 为准，不因持久化 mode 跳走；不写 storage，避免影响其它标签页的默认空间
      setMode(urlMode);
      return;
    }

    if (cb) return;

    // 无 URL 模式（如 /changeMode）：沿用持久化或唯一可用空间
    if (isConsoleMode(mode) && modeMap?.[mode]) {
      onClick(mode);
      return;
    }
    const onlyMode = Object.keys(modeMap)[0];
    if (isConsoleMode(onlyMode)) {
      onClick(onlyMode);
    }
  }, [loading, modeMap, resetMode, pathname]);

  const onClick = (m: ConsoleMode) => {
    setMode(m);
    persistConsoleMode(m);
    if (typeof cb === 'function') cb();
    navigate(modeMap?.[m], { replace: true });
  };

  return (
    <div className={styles.changeMode}>
      {MODE_ITEMS.map(({ key, image, navKey }) => {
        if (!modeMap[key]) return null;
        const nav = NAV_MAP[navKey];
        const title = nav?.title ?? '';
        const current = currentMode === key;
        return (
          <ModeCard
            key={key}
            image={image}
            title={title}
            description={nav?.description ?? ''}
            actionText={current ? t('navbar:current-space') : t('navbar:enter-mode', { name: title })}
            current={current}
            onClick={() => onClick(key as ConsoleMode)}
          />
        );
      })}
    </div>
  );
}

export function ChangeModeModal() {
  const { t } = useTranslation();
  const { setClusterList, setTenantClusters, tenant, isLogin, setMode, loading } = useContext(ConfigContext);
  const [open, setOpen] = useState(false);
  const { modeMap, loading: modeLoading } = useModeState();
  const navigate = useNavigate();
  const l = Object.keys(modeMap)?.length;
  const triggerTitle = t('common:console');
  const modalTitle = t('navbar:switch-management-space');

  useEffect(() => {
    if (!open) return;
    getClusterList();
    getTenantClusters0();
  }, [open]);

  useEffect(() => {
    if (!isLogin || loading || modeLoading || l) return;
    Message.error(t('login-page:no.permissions.available.please.contact.administrator'));
  }, [isLogin, loading, modeLoading, l, t]);

  // 获取当前用户的集群列表
  const getClusterList = async () => {
    const { data } = await getDcListAndVal({ isLogin: true });
    setClusterList(data);
  };

  // 获取当前租户的集群列表
  const getTenantClusters0 = async () => {
    const { data } = await getDcAndValByTenant({ tenant });
    setTenantClusters(data);
  };

  if (!isLogin) return null;

  return (
    <>
      <span
        onClick={() => {
          if (modeLoading) {
            setOpen(true);
            return;
          }
          if (!l) {
            Message.error(t('login-page:no.permissions.available.please.contact.administrator'));
            return;
          }
          if (l > 1 || modeMap.system) {
            setOpen(true);
          } else {
            setMode(Object.keys(modeMap)[0] as ConsoleMode);
            persistConsoleMode(Object.keys(modeMap)[0] as ConsoleMode);
            navigate(Object.values(modeMap)[0]);
          }
        }}
        className={`${styles['navbar-right-btn']}`}
      >
        {triggerTitle}
      </span>
      <Modal
        className={styles.changeModeModal}
        title={
          <div className={styles.changeModeModalHeader}>
            <div className={styles.changeModeModalTitle}>{modalTitle}</div>
            <div className={styles.changeModeModalSubtitle}>{t('navbar:select-management-space')}</div>
          </div>
        }
        visible={open}
        footer={null}
        autoFocus={false}
        onCancel={() => setOpen(false)}
        style={{ width: 760 }}
      >
        {modeLoading ? <Spin block /> : <ChangeModeContent modeMap={modeMap} cb={() => setOpen(false)} />}
      </Modal>
    </>
  );
}
